// A SHORT option set (5 or fewer) is a choice control, not a native select —
// brief §4.4 (P19, P42, P48, P64), 2026-09-24. Rebuilt 2026-09-26 as THE
// segmented control of the white-label redesign (DESIGN_SPEC §5.2): one
// capsule track (the `segmentTrack` theme — the brand's neutral plate
// `widgetPlate` in light, `layer`'s value in dark; 2026-09-30, see the track
// below), borderless segments, and the chosen segment in one of two
// strengths —
//
//   variant 'view'    a raised thumb (`raised` fill + the `float` elevation)
//                     for switching VIEWS: Contacts · Pipeline · Follow-ups.
//                     role=tablist / tab, aria-selected. Compact (36px).
//   variant 'choice'  the ink thumb (`highlight` / `highlight-reversed`, the
//                     D-1 inversion) for picking a VALUE: Person · Company.
//                     role=radiogroup / radio, aria-checked. 44px in a form;
//                     `compact: true` for a toolbar. The default.
//
// On a touch screen every segment draws 44px, either variant (the 44pt rule,
// AppSegmentedItem below); the sizes above are the pointer's.
//
// Selection is monochrome in three strengths (P5): rows take the `selected`
// wash, views the raised thumb, value choices the ink thumb. Hue never marks
// selection.
//
// TWO CONTRACTS, one look.
//
// THE ROW CONTRACT (new — frank-safe by construction: rows are plain data and
// the handler is a function NAME, so no row factory captures a loop variable):
//
//   AppSegmented: {
//     variant: 'view',
//     ariaLabel: '{{ crm.views | polyglot }}',
//     current: (el, s) => s.root.crmView || 'contacts', // reactive value
//     select: 'crmSetView',                             // el.call(select, value)
//     childrenAs: 'state',
//     children: () => [
//       { key: 'contacts', text: 'crm.view.contacts', value: 'contacts' }, …
//     ]
//   }
//
// `current`, not `value` (an HTML attribute name); `text` is a bundle key or a
// plain string (polyglot returns a non-key verbatim). Arrow keys move the
// choice and the focus (roving tabindex: only the chosen segment is in the Tab
// order), Home/End jump to the ends. On a narrow pane the track scrolls
// sideways — it never clips.
//
// THE OPTIONS CONTRACT (2026-09-24, unchanged for its call sites): `options`
// [{ value, label }] + `stateKey` — AppChoiceGroup's toggle semantics
// (role=group, aria-pressed). A call site with its own wiring overrides the
// segments' `isActive` / `ariaPressed` / `onClick` through `childProps`.
//
// HOW THE STATE REACHES THE CSS: every segment publishes `data-active` and
// `data-variant`, and the thumb is a STATIC selector rule keyed on them — not
// a `.isActive` block (create-only on this runtime) and not a factory (inline,
// and it cannot carry the dark/light theme pair).
//
// AppFieldSegmented is the FIELD variant: the AppField label above the
// control, the control named by the same label.
// THE TRACK IS AN AppScrollRow (overflow law §3b.5,
// DESIGN-WL-D2-KIT-OVERFLOW-LAW-1; crm-w1 measured the CRM view tabs cut at
// the screen edge at 390 px): a track wider than its room scrolls sideways
// with no scrollbar, fades ONLY the edge that hides a segment, and keeps the
// chosen segment whole — the kit's one edge-fade row (components/
// AppScrollRow.js), spread here because the track already extends
// AppChoiceGroup. It owns no state (the options contract reads the NEAREST
// state).
import { AppScrollRow } from './AppScrollRow.js'
import { touchRow } from './_touchTarget.js'

export const AppSegmented = {
  extends: 'AppChoiceGroup',
  variant: '',
  compact: false,
  role: (el) => (el.select ? (el.variant === 'view' ? 'tablist' : 'radiogroup') : 'group'),

  display: 'flex',
  flow: 'x',
  alignItems: 'stretch',
  width: 'fit-content',
  maxWidth: '100%',
  minHeight: (el) => (el.compact || el.variant === 'view' ? 'controlHCompact' : 'controlH'),
  boxSizing: 'border-box',
  padding: 'X',
  round: 'radiusPill',
  // THE TRACK (2026-09-30; Nika via "unreal crm": "the CRM submenu buttons
  // lose their background on other Windows devices"). Windows follows the
  // LIGHT scheme by default, where the old `layer` track read 1.08:1 on every
  // ground and the white view thumb 1.08-1.22:1 on it. `segmentTrack`
  // (shared designSystem/theme.js): light = the brand's neutral plate
  // `widgetPlate` (1.15:1 on every ground, the thumb 1.15-1.30:1); dark =
  // `layer`'s value, unchanged (widgetPlate's dark half equals the dark thumb
  // on a dark sheet, 1.08:1). __tests__/segmentedTrackContrast.test.js.
  theme: 'segmentTrack',
  // the edge-fade row: min-width 0 (a flex item's default is its content),
  // sideways scroll with no scrollbar, the fade, its scope and its hooks
  ...AppScrollRow,
  scrollSnapType: 'x proximity',

  childExtends: 'AppSegmentedItem',
  childProps: {
    // Both contracts in one place; `childProps` overrides AppChoiceGroup's
    // option-only versions of the first three.
    isActive: (el, s) => {
      const seg = el.parent || {}
      if (seg.select) return seg.current === (s && s.value)
      const key = seg.stateKey
      return !!key && s?.[key] === el.optionValue
    },
    ariaPressed: (el) => (el.parent && el.parent.select ? null : el.isActive ? 'true' : 'false'),
    onClick: (e, el, s) => {
      const seg = el.parent || {}
      if (seg.select) return el.call(seg.select, s && s.value)
      const key = seg.stateKey
      if (key) s.update({ [key]: el.optionValue })
    }
  }
}

// ONE segment. The look and the keyboard; the choice logic arrives from the
// track's `childProps` (and a call site may override it there).
export const AppSegmentedItem = {
  extends: 'AppControl',
  compact: true,
  tag: 'button',
  type: 'button',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: '0',
  // A segment is a control: the house compact height (Nika 2026-09-26 12:2x,
  // "Keep the rule: segments use the 36 px compact height" — the admin
  // control law flagged the old `minHeight: '0'` on every AppSegmented tab).
  // The track's X inset around a 36 px segment makes the track 44 px.
  minHeight: 'controlHCompact',
  // ON A TOUCH SCREEN a segment DRAWS the 44pt floor on both axes
  // (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1; _touchTarget.js `touchRow`) — the
  // pointer keeps the 36px rule above. Segments sit flush in a track that
  // scrolls, so a pseudo hit area would take a neighbour's edge (a short
  // label like "7D" is under 44 wide) and be cut by the track; the segment's
  // own box is its hit area instead. Measured on dev DFI 2026-10-05 with a
  // touch device simulated: the home board's Next up switcher's segments
  // 36px tall, and the Validators lane had to draw its own segments at 44
  // (ValSeg). The track keeps its X inset, so it grows to 44 + 2 X around
  // them.
  '@coarse': { ...touchRow },
  padding: '0 A',
  round: 'radiusPill',
  borderWidth: '0',
  background: 'transparent',
  fontSize: 'Z',
  fontWeight: '500',
  lineHeight: '1',
  color: 'caption',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  scrollSnapAlign: 'start',
  transition: 'background-color A defaultBezier, color A defaultBezier',

  text: (el, s) =>
    el.call('polyglot', (el.optionValue !== undefined ? el.optionLabel : s && s.text) ?? ''),

  // The row contract's semantics; the options contract keeps the toggle
  // (aria-pressed from the track's childProps) and native Tab order.
  role: (el) => {
    const seg = el.parent || {}
    if (!seg.select) return null
    return seg.variant === 'view' ? 'tab' : 'radio'
  },
  ariaSelected: (el) => {
    const seg = el.parent || {}
    return seg.select && seg.variant === 'view' ? (el.isActive ? 'true' : 'false') : null
  },
  ariaChecked: (el) => {
    const seg = el.parent || {}
    return seg.select && seg.variant !== 'view' ? (el.isActive ? 'true' : 'false') : null
  },
  tabindex: (el) => (el.parent && el.parent.select ? (el.isActive ? '0' : '-1') : null),

  // Flat `dataX` hooks, never a `data: {}` block a call-site `data` object
  // would replace (FW-ELEMENT-DEFINITION-DATA-REPLACES-EXTENDS-DATA-1).
  dataActive: (el) => !!el.isActive,
  dataVariant: (el) => (el.parent && el.parent.variant === 'view' ? 'view' : 'choice'),

  // The raised thumb: the `raised` fill (white; `gray5` in dark, where the
  // sheet colour would read SUNKEN on a track washed lighter than the sheet)
  // and the `float` elevation. The shadow is the token's CSS variable, not
  // the `shadow:` prop — a [light, dark] shadow pair nested in a selector
  // block compiles to an invalid `selector @media …` rule; the variable flips
  // with the scheme by itself.
  //
  // EVERY STATE COLOUR IS THE BRAND TOKEN'S CSS VARIABLE, for the same reason
  // and one more (2026-10-25, Nika: "Yes, this fix only"; the CRM's contact
  // type and filters on dev). An installed app renders its pages under its OWN
  // isolated config — its tokens plus default-config, `useVariable: false`
  // (workspace functions/workspaceProjects.js, the module design system).
  // That config has no `highlight`, `highlight-reversed`, `raised`, `hover`
  // or `selected`, so the token names went out as raw words:
  // `background: highlight` (a CSS system colour) under an invalid
  // `color: highlight-reversed` — the caption ink stayed, and the chosen
  // segment's label vanished into the fill. The variables are the shell's
  // own (the brand emits them on the document in both schemes), so they
  // resolve the same in the shell and inside every app: the ink thumb is
  // black on white / white on black (21:1), the view thumb the title ink on
  // `raised` — __tests__/appSegmentedStateTokens.test.js.
  '&[data-active][data-variant="view"]': {
    background: 'var(--color-raised)',
    color: 'var(--color-title)',
    boxShadow: 'var(--shadow-float)'
  },
  '&[data-active][data-variant="choice"]': {
    background: 'var(--color-highlight)',
    color: 'var(--color-highlight-reversed)'
  },
  '&:not([data-active]):hover': {
    background: 'var(--color-hover)',
    color: 'var(--color-title)'
  },
  '&:not([data-active]):active': { background: 'var(--color-selected)' },

  // WINDOWS CONTRAST (forced colours; the shared `forcedColors` media): the
  // browser repaints every fill with a system colour and drops every
  // box-shadow, so both thumbs (a fill, a fill + elevation) and the focus
  // ring (a shadow) vanish. System-colour outlines carry them instead — the
  // only colours that mode keeps; there is no design-system token for them.
  // Longhands: the `outline` shorthand reads its words as spacing tokens.
  // Widths on the size token `hairline2` (2px — the kit holds no raw px).
  '@forcedColors': {
    '&[data-active]': {
      outlineWidth: 'hairline2',
      outlineStyle: 'solid',
      outlineColor: 'Highlight',
      outlineOffset: '0'
    },
    '&:focus-visible': {
      outlineWidth: 'hairline2',
      outlineStyle: 'solid',
      outlineColor: 'CanvasText',
      outlineOffset: 'hairline2'
    }
  },

  // Roving focus: ← → (and ↑ ↓) move to the neighbour, Home / End to the
  // ends; the move IS the choice (automatic activation), and focus follows.
  // The segment that BECOMES the choice (a click, the keyboard, or a value
  // changed from outside) is brought into view in its track. `stateDeps` +
  // `onStateUpdate` is the framework's declarative effect: it fires only when
  // `isActive` actually flips, never on the first render.
  stateDeps: [(el) => !!el.isActive],
  onStateUpdate: (el, s, ctx, change) => {
    const seg = el.parent
    if (!change || !change.next || !change.next[0] || !seg || !seg.scope) return
    if (typeof seg.scope.edgeFadeBecameCurrent === 'function') {
      seg.scope.edgeFadeBecameCurrent(seg, el.node)
    }
  },

  onKeydown: (e, el) => {
    const seg = el.parent
    if (!seg || !seg.select) return
    const items = typeof seg.getChildren === 'function' ? seg.getChildren() : []
    const at = items.indexOf(el)
    if (at === -1) return
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]
    let next = null
    if (step) next = items[(at + step + items.length) % items.length]
    else if (e.key === 'Home') next = items[0]
    else if (e.key === 'End') next = items[items.length - 1]
    if (!next || next === el) return
    e.preventDefault()
    el.call(seg.select, next.state && next.state.value)
    if (next.node && next.node.focus) next.node.focus()
  }
}

export const AppFieldSegmented = {
  extends: 'AppField',
  // A choice group is not a <label>: a label's click would activate its
  // first button (buttons are labelable) and pick a segment by accident.
  tag: 'div',
  AppSegmented: {
    ariaLabel: (el) => el.call('polyglot', el.parent?.label || ''),
    options: (el) => el.parent?.options,
    stateKey: (el) => el.parent?.stateKey
  }
}
